// src/components/ShareModal.tsx
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCopy,
  FaEnvelope,
  FaFacebookF,
  FaLink,
  FaTelegram,
  FaTimes,
  FaTwitter,
  FaWhatsapp,
} from "react-icons/fa";
import type { Branch } from "~/types/new_types";
import { getBranchName } from "~/utils/brandHelper";

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  branch: Branch | null;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, branch }) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);

  // Generate the share URL
  const shareUrl = window.location.href;
  const shareTitle = `Check out ${branch?.name}`;
  const shareDescription = branch?.description;

  // Share methods
  const shareOptions = [
    {
      id: "copy",
      label: t("share.copyLink"),
      icon: <FaCopy />,
      color: "#6B7280",
      action: async () => {
        try {
          await navigator.clipboard.writeText(shareUrl);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error("Failed to copy: ", err);
        }
      },
    },
    {
      id: "whatsapp",
      label: t("share.whatsapp"),
      icon: <FaWhatsapp />,
      color: "#25D366",
      action: () => {
        const text = `${shareTitle} - ${shareDescription}\n${shareUrl}`;
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`,
          "_blank"
        );
      },
    },
    {
      id: "facebook",
      label: t("share.facebook"),
      icon: <FaFacebookF />,
      color: "#1877F2",
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            shareUrl
          )}`,
          "_blank"
        );
      },
    },
    {
      id: "twitter",
      label: t("share.twitter"),
      icon: <FaTwitter />,
      color: "#1DA1F2",
      action: () => {
        const text = `${shareTitle} ${shareUrl}`;
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
          "_blank"
        );
      },
    },
    {
      id: "telegram",
      label: t("share.telegram"),
      icon: <FaTelegram />,
      color: "#0088CC",
      action: () => {
        const text = `${shareTitle} - ${shareDescription}\n${shareUrl}`;
        window.open(
          `https://t.me/share/url?url=${encodeURIComponent(
            shareUrl
          )}&text=${encodeURIComponent(text)}`,
          "_blank"
        );
      },
    },
    {
      id: "email",
      label: t("share.email"),
      icon: <FaEnvelope />,
      color: "#EA4335",
      action: () => {
        const subject = encodeURIComponent(shareTitle);
        const body = encodeURIComponent(`${shareDescription}\n\n${shareUrl}`);
        window.open(`mailto:?subject=${subject}&body=${body}`);
      },
    },
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
    }

    return () => {
      document.body.style.overflow = "visible";
    };
  }, [isOpen]);

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: {
        duration: 0.2,
      },
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <div 
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-xs md:max-w-md text-black"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl overflow-hidden"
              style={{ borderRadius: branch?.theme.borderRadius }}
            >
              {/* Header */}
              <div
                className="p-4 border-b flex items-center justify-between"
                style={{ borderColor: `${branch?.theme.borderColor}40` }}
              >
                <h2 className="text-xl font-bold">{t("share.title")}</h2>
                <motion.button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaTimes className="text-gray-500" />
                </motion.button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Restaurant Info */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0"
                    style={{ borderRadius: branch?.theme.borderRadius }}
                  >
                    <img
                      src={branch?.logo || "/images/add-photo.jpg"}
                      alt={branch?.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/images/add-photo.jpg";
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="font-bold">
                      {getBranchName(branch, i18n.language)}
                    </h3>
                  </div>
                </div>

                {/* URL Preview */}
                <div
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-6"
                  style={{ borderRadius: branch?.theme.borderRadius }}
                >
                  <FaLink className="text-gray-400" />
                  <span className="flex-1 text-sm text-gray-600 truncate">
                    {shareUrl}
                  </span>
                  <motion.button
                    onClick={shareOptions[0].action}
                    className="p-2 rounded hover:bg-gray-100 transition-colors cursor-pointer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <motion.div
                      animate={copied ? { scale: [1, 1.2, 1] } : {}}
                      transition={{ duration: 0.3 }}
                    >
                      {copied ? (
                        <span className="text-green-600 text-sm">✓</span>
                      ) : (
                        <FaCopy className="text-gray-400" />
                      )}
                    </motion.div>
                  </motion.button>
                </div>

                {/* Share Options */}
                <motion.div
                  className="grid grid-cols-3 gap-4"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {shareOptions.map((option) => (
                    <motion.button
                      key={option.id}
                      onClick={option.action}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{ borderRadius: branch?.theme.borderRadius }}
                    >
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl"
                        style={{ backgroundColor: option.color }}
                      >
                        {option.icon}
                      </div>
                      <span className="text-sm text-gray-600">
                        {option.label}
                      </span>
                    </motion.button>
                  ))}
                </motion.div>
              </div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShareModal;
